import { useState, useEffect, useCallback } from "react";
import { useCall } from "./CallContext";
import {
  PhoneIcon,
  VideoIcon,
  MicrophoneIcon,
  MuteMicIcon,
  CameraOffIcon,
  ScreenShareIcon,
  EndCallIcon,
} from "../../svg/chat";
import "./ActiveCall.css";

export default function ActiveCall() {
  const {
    callState,
    mediaState,
    localVideoRef,
    remoteVideoRef,
    endCall,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
  } = useCall();

  const [callDuration, setCallDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Format duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Call timer
  useEffect(() => {
    if (!callState.isInCall) {
      setCallDuration(0);
      return;
    }

    const interval = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [callState.isInCall]);

  // Hide controls after 3 seconds of inactivity
  useEffect(() => {
    if (!showControls) return;

    const timeout = setTimeout(() => {
      setShowControls(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [showControls]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.error);
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(console.error);
      setIsFullscreen(false);
    }
  }, []);

  // Handle mouse move to show controls
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
  }, []);

  // Don't render if not in call or just ringing
  if (!callState.isInCall && !callState.isRinging) {
    return null;
  }

  // Ringing screen (outgoing call)
  if (callState.isRinging && !callState.isIncoming) {
    const targetName = callState.targetUser?.first_name
      ? `${callState.targetUser.first_name} ${callState.targetUser.last_name || ""}`
      : "Usuario";

    return (
      <div className="active_call_container ringing">
        <div className="call_background">
          <div className="background_blur"></div>
        </div>

        <div className="ringing_content">
          <div className="call_type_indicator">
            {callState.callType === "video" ? (
              <VideoIcon color="#fff" />
            ) : (
              <PhoneIcon color="#fff" />
            )}
            <span>
              {callState.callType === "video" ? "Chamada de video" : "Chamada de voz"}
            </span>
          </div>

          <div className="calling_user">
            <div className="calling_avatar">
              <img
                src={callState.targetUser?.picture || "/images/default_user.png"}
                alt=""
              />
            </div>
            <h2>{targetName}</h2>
            <p className="calling_status">Chamando...</p>
          </div>

          <div className="local_video_preview">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
            />
          </div>

          <div className="ringing_actions">
            <button className="end_call_btn" onClick={endCall}>
              <EndCallIcon color="#fff" />
              <span>Cancelar</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active call screen
  return (
    <div
      className="active_call_container"
      onMouseMove={handleMouseMove}
      onClick={() => setShowControls(true)}
    >
      {/* Remote video (full screen) */}
      <div className="remote_video_container">
        {callState.callType === "video" ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="remote_video"
          />
        ) : (
          <div className="voice_call_background">
            <div className="voice_call_avatar">
              <img
                src={callState.caller?.picture || "/images/default_user.png"}
                alt=""
              />
            </div>
            <h2 className="voice_call_name">
              {callState.caller?.name ||
                `${callState.caller?.first_name || ""} ${callState.caller?.last_name || ""}`.trim() ||
                "Usuario"}
            </h2>
          </div>
        )}

        {/* Remote stream fallback message */}
        {callState.callType === "video" && !mediaState.remoteStream && (
          <div className="waiting_for_video">
            <div className="spinner"></div>
            <p>Conectando video...</p>
          </div>
        )}
      </div>

      {/* Local video (picture in picture) */}
      {callState.callType === "video" && (
        <div className="local_video_pip">
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
          />
          {!mediaState.isVideoEnabled && (
            <div className="video_off_overlay">
              <CameraOffIcon color="#fff" />
            </div>
          )}
        </div>
      )}

      {/* Call info header */}
      <div className={`call_header ${showControls ? "visible" : ""}`}>
        <div className="call_info">
          <span className="call_duration">{formatDuration(callDuration)}</span>
          <span className="call_quality">HD</span>
        </div>
        <button className="fullscreen_btn" onClick={toggleFullscreen}>
          {isFullscreen ? "Sair" : "Tela cheia"}
        </button>
      </div>

      {/* Call controls */}
      <div className={`call_controls ${showControls ? "visible" : ""}`}>
        <button
          className={`control_btn ${!mediaState.isAudioEnabled ? "off" : ""}`}
          onClick={toggleAudio}
          title={mediaState.isAudioEnabled ? "Desativar microfone" : "Ativar microfone"}
        >
          {mediaState.isAudioEnabled ? (
            <MicrophoneIcon color="#fff" />
          ) : (
            <MuteMicIcon color="#fff" />
          )}
          <span>{mediaState.isAudioEnabled ? "Mudo" : "Ativar"}</span>
        </button>

        {callState.callType === "video" && (
          <button
            className={`control_btn ${!mediaState.isVideoEnabled ? "off" : ""}`}
            onClick={toggleVideo}
            title={mediaState.isVideoEnabled ? "Desativar camera" : "Ativar camera"}
          >
            {mediaState.isVideoEnabled ? (
              <VideoIcon color="#fff" />
            ) : (
              <CameraOffIcon color="#fff" />
            )}
            <span>{mediaState.isVideoEnabled ? "Camera" : "Ligar"}</span>
          </button>
        )}

        {callState.callType === "video" && (
          <button
            className={`control_btn ${mediaState.isScreenSharing ? "active" : ""}`}
            onClick={mediaState.isScreenSharing ? stopScreenShare : startScreenShare}
            title={mediaState.isScreenSharing ? "Parar compartilhamento" : "Compartilhar tela"}
          >
            <ScreenShareIcon color="#fff" />
            <span>{mediaState.isScreenSharing ? "Parar" : "Tela"}</span>
          </button>
        )}

        <button className="control_btn end_call" onClick={endCall} title="Encerrar chamada">
          <EndCallIcon color="#fff" />
          <span>Encerrar</span>
        </button>
      </div>
    </div>
  );
}
